import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import IconSave from '../../components/icons/IconSave';
import TagsEditor from '../../components/forms/TagsEditor';
import language from '../../variables/language';
import '../../css/modals.css';

const BLANK = { word: '', partOfSpeechId: '', tenseId: '', formId: '', frequency: '', translations: [], transcriptionBr: '', transcriptionUs: '' };

const flattenWord = (data) => ({
   word: data.word || '',
   partOfSpeechId: data.partOfSpeech ? String(data.partOfSpeech.id) : '',
   tenseId: data.tense ? String(data.tense.id) : '',
   formId: data.form ? String(data.form.id) : '',
   frequency: data.frequency != null ? String(data.frequency) : '',
   translations: Array.isArray(data.translations) ? data.translations.map(t => t.translation) : [],
   transcriptionBr: data.transcriptionBr || '',
   transcriptionUs: data.transcriptionUs || '',
});

// unified create/edit form - wordId null means "brand new word" (word text
// editable, saves via create-with-new-word), wordId set means "complete/fix
// an existing word's attributes" (word text locked, saves via PUT /api/words).
// initialWordText seeds the word field when opened as a create-mode quick-add
// (e.g. from LessonDetailModal's "no match in dictionary" +AI button) - it's
// treated as the user's own first edit (isDirty right away, same as if they'd
// just typed it themselves), not as a pre-existing saved value.
export default function WordEditModal({ isOpen, wordId, studentId, initialWordText, onClose, onSaved }) {
   const navigate = useNavigate();
   const lang = language[language.lang].wordsTab;
   const langRoot = language[language.lang];

   const [formValues, setFormValues] = useState(BLANK);
   const [initialValues, setInitialValues] = useState(BLANK);
   const [partsOfSpeech, setPartsOfSpeech] = useState([]);
   const [tenses, setTenses] = useState([]);
   const [forms, setForms] = useState([]);
   const [saving, setSaving] = useState(false);
   const [generating, setGenerating] = useState(false);
   const [aiError, setAiError] = useState('');
   const [suggestedSpellings, setSuggestedSpellings] = useState([]);

   useEffect(() => {
      if(!isOpen) {
         return;
      }
      axios.get('/api/partsofspeech', { withCredentials: true }).then(r => setPartsOfSpeech(Array.isArray(r.data) ? r.data : [])).catch(() => {});
      axios.get('/api/tenses', { withCredentials: true }).then(r => setTenses(Array.isArray(r.data) ? r.data : [])).catch(() => {});
      axios.get('/api/forms', { withCredentials: true }).then(r => setForms(Array.isArray(r.data) ? r.data : [])).catch(() => {});
   }, [isOpen]);

   useEffect(() => {
      if(!isOpen) {
         setFormValues(BLANK);
         setInitialValues(BLANK);
         setAiError('');
         setSuggestedSpellings([]);
         return;
      }
      if(wordId) {
         axios.get('/api/words/' + wordId, { withCredentials: true })
            .then(response => {
               const flat = flattenWord(response.data);
               setFormValues(flat);
               setInitialValues(flat);
            })
            .catch(err => { if(err.response && err.response.status === 401) navigate('/login'); });
      } else if(initialWordText) {
         setFormValues({ ...BLANK, word: initialWordText });
         setInitialValues(BLANK);
      } else {
         setFormValues(BLANK);
         setInitialValues(BLANK);
      }
      setAiError('');
      setSuggestedSpellings([]);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [isOpen, wordId, initialWordText]);

   const isDirty = useMemo(() => JSON.stringify(formValues) !== JSON.stringify(initialValues), [formValues, initialValues]);

   if(!isOpen) return '';

   const stopProp = e => e.stopPropagation();

   const handleFieldChange = (name, value) => {
      setFormValues(prev => ({...prev, [name]: value}));
   }
   const handleDiscard = () => setFormValues(initialValues);

   const handlePickSpelling = (spelling) => {
      handleFieldChange('word', spelling);
      setSuggestedSpellings([]);
   }

   const handleAiAssist = () => {
      if(!formValues.word.trim() || generating) {
         return;
      }
      setGenerating(true);
      setAiError('');
      setSuggestedSpellings([]);
      axios.post('/api/ai/classify-word', JSON.stringify({ word: formValues.word.trim() }), {
         withCredentials: true,
         headers: { 'Content-Type': 'application/json' },
      }).then(response => {
         const r = response.data;
         setFormValues(prev => ({
            ...prev,
            partOfSpeechId: r.partOfSpeechId != null ? String(r.partOfSpeechId) : prev.partOfSpeechId,
            tenseId: r.tenseId != null ? String(r.tenseId) : prev.tenseId,
            formId: r.formId != null ? String(r.formId) : prev.formId,
            frequency: r.frequency != null ? String(r.frequency) : prev.frequency,
            translations: (r.translations && r.translations.length) ? r.translations : prev.translations,
            transcriptionBr: r.transcriptionBr || prev.transcriptionBr,
            transcriptionUs: r.transcriptionUs || prev.transcriptionUs,
         }));
         setSuggestedSpellings(Array.isArray(r.suggestedSpellings) ? r.suggestedSpellings : []);
      }).catch(err => {
         if(err.response && err.response.status === 401){
            navigate('/login');
            return;
         }
         setAiError((err.response && err.response.data) || lang.aiAssistError);
      }).finally(() => setGenerating(false));
   }

   const buildWordPayload = () => ({
      word: formValues.word.trim(),
      partOfSpeech: formValues.partOfSpeechId || null,
      tense: formValues.tenseId || null,
      form: formValues.formId || null,
      frequency: formValues.frequency !== '' ? Number(formValues.frequency) : null,
      translations: formValues.translations.map(text => ({ translation: text })),
      transcriptionBr: formValues.transcriptionBr || null,
      transcriptionUs: formValues.transcriptionUs || null,
      verified: true,
   });

   const handleSave = () => {
      if(!formValues.word.trim() || saving) {
         return;
      }
      setSaving(true);
      const request = wordId
         ? axios.put('/api/words/' + wordId, JSON.stringify(buildWordPayload()), { withCredentials: true, headers: {'Content-Type':'application/json'} })
         : studentId
            ? axios.post('/api/wordstudents/create-with-new-word', JSON.stringify({ student: studentId, word: buildWordPayload() }), { withCredentials: true, headers: {'Content-Type':'application/json'} })
            : axios.post('/api/words', JSON.stringify(buildWordPayload()), { withCredentials: true, headers: {'Content-Type':'application/json'} });
      request.then((response) => {
         onSaved(response.data);
      }).catch(err => {
         if(err.response && err.response.status === 401){
            navigate('/login');
         }
         console.log(err);
      }).finally(() => setSaving(false));
   }

   return (
      <div className="modal-menu" onClick={onClose} style={{ display: 'flex' }}>
         <div className="modal-window word-edit-window" onClick={stopProp}>
            <h3 className="modal-w-title">{wordId ? lang.word : lang.addNew}</h3>

            <div className='student-card-section'>
               <div className='student-card-fields-grid'>
                  <div className='student-card-field'>
                     {suggestedSpellings.length > 0 ? (
                        <div className='word-edit-spelling-suggestions'>
                           <span className='word-edit-spelling-suggestions-label'>{lang.spellingSuggestionLabel}</span>
                           {suggestedSpellings.map(s => (
                              <span key={s} className='word-edit-spelling-suggestion-chip' onClick={() => handlePickSpelling(s)}>{s}</span>
                           ))}
                        </div>
                     ) : ''}
                     <div className='flex-from-input-wraper' data-label={lang.word}>
                        <input className='submit-field from-input txt-c' type='text' placeholder={lang.word}
                           value={formValues.word} disabled={!!wordId}
                           onChange={(e) => handleFieldChange('word', e.target.value)} />
                     </div>
                  </div>
                  <div className='flex-from-input-wraper student-card-field' data-label={lang.ukTranslation}>
                     <TagsEditor tags={formValues.translations}
                        onChange={(next) => handleFieldChange('translations', next)}
                        placeholder={lang.translationsPlaceholder} emptyLabel={lang.noTranslations} />
                  </div>
               </div>
               <div className='student-card-fields-grid'>
                  <div className='flex-from-input-wraper student-card-field-sm' data-label={lang.partOfSpeech}>
                     <select className='submit-field form-selection txt-c' value={formValues.partOfSpeechId}
                        onChange={(e) => handleFieldChange('partOfSpeechId', e.target.value)}>
                        <option value=''>{langRoot['not selected']}</option>
                        {partsOfSpeech.map(p => <option key={p.id} value={p.id}>{p.description}</option>)}
                     </select>
                  </div>
                  <div className='flex-from-input-wraper student-card-field-sm' data-label={lang.tense}>
                     <select className='submit-field form-selection txt-c' value={formValues.tenseId}
                        onChange={(e) => handleFieldChange('tenseId', e.target.value)}>
                        <option value=''>{langRoot['not selected']}</option>
                        {tenses.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                     </select>
                  </div>
                  <div className='flex-from-input-wraper student-card-field-sm' data-label={lang.form}>
                     <select className='submit-field form-selection txt-c' value={formValues.formId}
                        onChange={(e) => handleFieldChange('formId', e.target.value)}>
                        <option value=''>{langRoot['not selected']}</option>
                        {forms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                     </select>
                  </div>
                  <div className='flex-from-input-wraper student-card-field-sm' data-label={lang.frequency}>
                     <input className='submit-field from-input txt-c' type='number' step='0.01' placeholder={lang.frequency}
                        value={formValues.frequency}
                        onChange={(e) => handleFieldChange('frequency', e.target.value)} />
                  </div>
               </div>
               <div className='student-card-fields-grid'>
                  <div className='flex-from-input-wraper student-card-field' data-label={lang.transcriptionBr}>
                     <input className='submit-field from-input txt-c' type='text' placeholder={lang.transcriptionBr}
                        value={formValues.transcriptionBr}
                        onChange={(e) => handleFieldChange('transcriptionBr', e.target.value)} />
                  </div>
                  <div className='flex-from-input-wraper student-card-field' data-label={lang.transcriptionUs}>
                     <input className='submit-field from-input txt-c' type='text' placeholder={lang.transcriptionUs}
                        value={formValues.transcriptionUs}
                        onChange={(e) => handleFieldChange('transcriptionUs', e.target.value)} />
                  </div>
               </div>
            </div>

            <div className='word-edit-ai-row'>
               <button type='button' className='word-edit-ai-btn' disabled={!formValues.word.trim() || generating} onClick={handleAiAssist}>
                  {generating ? lang.aiAssistGenerating : lang.aiAssist}
               </button>
               {aiError ? <span className='word-edit-ai-error'>{aiError}</span> : ''}
            </div>

            {isDirty ? (
               <div className='student-card-form-footer'>
                  <span className='student-card-unsaved-hint'>{langRoot.unsavedChanges}</span>
                  <div className='student-card-discard-link' onClick={handleDiscard}>{langRoot.cancel}</div>
                  <button type='button' className='student-card-save-btn' disabled={saving || !formValues.word.trim()} onClick={handleSave}>
                     <IconSave />
                     {saving ? langRoot['Loading ...'] : langRoot.save}
                  </button>
               </div>
            ) : ''}
         </div>
      </div>
   );
}
